import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import { buildResume, RESUME_VARIANTS } from '../../resume-engine/buildResume';
import type { ResumeLayoutMode, ResumeVariantId } from '../../resume-engine/types';
import { ProfessionalLayout } from './layouts/ProfessionalLayout';
import { ATSLayout } from './layouts/ATSLayout';
import { ExecutiveLayout } from './layouts/ExecutiveLayout';
import { PrintLayout } from './layouts/PrintLayout';
import './resume.scss';

function ResumePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [exporting, setExporting] = useState<'pdf' | 'docx' | null>(null);

  const variant = (searchParams.get('variant') as ResumeVariantId) || 'fullstack';
  const layout = (searchParams.get('layout') as ResumeLayoutMode) || 'professional';

  const resume = useMemo(() => buildResume(variant, layout), [variant, layout]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next);
  };

  const handleExport = async (type: 'pdf' | 'docx') => {
    setExporting(type);
    try {
      const mod = await import('../../resume-engine/export');
      if (type === 'pdf') await mod.exportResumePdf(resume, layout);
      else await mod.exportResumeDocx(resume, layout);
    } catch (err) {
      console.error(err);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(null);
    }
  };

  const printUrl = `/resume/print?variant=${variant}&layout=${layout === 'ats' || layout === 'executive' ? layout : 'print'}`;

  return (
    <div className="resume-page">
      <div className="resume-toolbar no-print">
        <div className="resume-toolbar-left">
          <Link to="/" className="resume-toolbar-link">
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Portfolio
          </Link>
          <span className="resume-toolbar-title">Resume Generator</span>
        </div>

        <div className="resume-toolbar-controls">
          <label className="resume-control">
            <span>Role focus</span>
            <select value={variant} onChange={(e) => updateParam('variant', e.target.value)}>
              {RESUME_VARIANTS.map((v) => (
                <option key={v.id} value={v.id}>{v.label}</option>
              ))}
            </select>
          </label>

          <label className="resume-control">
            <span>Layout</span>
            <select value={layout} onChange={(e) => updateParam('layout', e.target.value)}>
              <option value="professional">Professional</option>
              <option value="executive">Executive (1 page)</option>
              <option value="ats">ATS</option>
              <option value="print">Print</option>
            </select>
          </label>
        </div>

        <div className="resume-toolbar-actions">
          <Link to="/cover-letter" className="resume-btn resume-btn-secondary">
            Cover Letter Generator
          </Link>
          <button type="button" className="resume-btn" onClick={() => handleExport('pdf')} disabled={!!exporting}>
            <DownloadIcon sx={{ fontSize: 16 }} />
            {exporting === 'pdf' ? 'Exporting…' : 'Download PDF'}
          </button>
          <button type="button" className="resume-btn resume-btn-secondary" onClick={() => handleExport('docx')} disabled={!!exporting}>
            <DescriptionIcon sx={{ fontSize: 16 }} />
            {exporting === 'docx' ? 'Exporting…' : 'Download Word (.doc)'}
          </button>
          <Link to={printUrl} className="resume-btn resume-btn-secondary" target="_blank" rel="noreferrer">
            <PrintIcon sx={{ fontSize: 16 }} />
            Open Print View
          </Link>
        </div>
      </div>

      <div className={`resume-canvas${layout === 'print' ? ' resume-canvas--print' : ''}`}>
        {layout === 'ats' ? (
          <ATSLayout resume={resume} />
        ) : layout === 'executive' ? (
          <ExecutiveLayout resume={resume} />
        ) : layout === 'print' ? (
          <PrintLayout resume={resume} />
        ) : (
          <ProfessionalLayout resume={resume} />
        )}
      </div>
    </div>
  );
}

export default ResumePage;
